/**
 * SIngleProduct.js — Single product detail page router.
 *
 * Fetches the product data from Shopify Storefront API using the URL slug,
 * parses the engine metafield, and delegates rendering to the appropriate
 * type-specific component:
 *  - TypeProgram  → RecordType "CPD Programs" (and any unrecognised type)
 *  - TypeBook     → RecordType "Book"
 *  - TypeWebcast  → RecordType "Online Intensive"
 *
 * Props:
 *  - AddtoCart {Function}               Adds a variant to the cart.
 *  - AddtoWishlist {Function}           Toggles a product in the wishlist.
 *  - getAllWishlist {Array}             Current wishlist slugs.
 *  - EngineURL {string}                Base URL for the engine API.
 *  - Program_structure_images {Array}  Slug-to-image-URL mapping.
 *  - Program_structure_color {Array}   Slug-to-colour mapping.
 *  - UpdateCartQty {Function}          Updates the header cart badge.
 *  - UpdateCartAmount {Function}       Updates the header cart total.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Helmet } from "react-helmet";
import SwiperCore, { Navigation } from 'swiper';
import { Container } from 'react-bootstrap';

// ─── Internal — type-specific layouts ────────────────────────────────────────
import TypeProgram from './Single-program-types/type-program';
import TypeBook from './Single-program-types/type-book';
import TypeWebcast from './Single-program-types/type-webcast';

// ─── Internal — GraphQL & icons ──────────────────────────────────────────────
import { FetchProductData } from '../grappql/Product/Queries/Product';
import { LoaderIcon } from "../../icons";
import { sanitizeText } from '../../utils/text';

// ─── Swiper setup ─────────────────────────────────────────────────────────────
SwiperCore.use([Navigation]);

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * SIngleProduct — product detail page.
 * Fetches data by slug from the URL, then renders the correct type layout.
 * See file-level JSDoc for full props description.
 */
export const SIngleProduct = (props) => {

  // ── Product image / colour for the program type ─────────────────────────
  const [Program_structure_image, setProgram_structure_image] = useState([
    'https://engine.leocussen.edu.au/sync/img/logo_updated.png'
  ]);


  // ── Core product state ────────────────────────────────────────────────────
  const [relatedProducts] = useState([]);
  const [recordType, setRecordType] = useState('');
  const [programType, setprogramType] = useState(undefined);
  const [quantity, setQuantity] = useState(1);
  const [priceRange] = useState(0);
  const [compareAtPriceRange] = useState(0);
  const [SingleVariants, setSingleVariant] = useState();
  const [seo_title, set_seo_title] = useState('');
  const [seo_description, set_seo_description] = useState('');
  const [reached_capacity, setreached_capacity] = useState(false);

  // ── Engine metafield data ──────────────────────────────────────────────────
  const [single_program_data, set_single_program_data] = useState([]);
  const [single_program_basic_data, set_single_program_basic_data] = useState([]);

  // ── Cart consistency ──────────────────────────────────────────────────────
  // Single source of truth for quantity is App's shared cart (CheckoutData).
  // If the currently shown variant is already in the cart, the product page
  // mirrors that line's quantity and edits it directly, so the cart sidebar,
  // delivery page and this page always stay in sync.
  const cartLineForVariant = (() => {
    const variantId = SingleVariants && SingleVariants.id;
    const edges =
      props.CheckoutData && props.CheckoutData.cart && props.CheckoutData.cart.lines
        ? props.CheckoutData.cart.lines.edges
        : null;
    if (!variantId || !edges) return null;
    const match = edges.find(
      (e) => e.node.merchandise && e.node.merchandise.id === variantId
    );
    return match ? { lineId: match.node.id, quantity: match.node.quantity } : null;
  })();

  const lineId = cartLineForVariant?.lineId ?? null;
  const lineQuantity = cartLineForVariant?.quantity ?? null;

  // Reflect the cart's quantity here whenever it changes elsewhere (sidebar /
  // delivery page). When the variant isn't in the cart, the local "quantity to
  // add" value is left untouched.
  useEffect(() => {
    if (cartLineForVariant) setQuantity(cartLineForVariant.quantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId, lineQuantity]);

  /** Pushes a new quantity to the shared cart when the variant is already in it. */
  const syncQtyToCart = (qty) => {
    if (cartLineForVariant && props.UpdateCartItem) {
      props.UpdateCartItem(qty, cartLineForVariant.lineId);
    }
  };

  // ── Quantity controls ─────────────────────────────────────────────────────

  /** Increments product quantity (called by + button). */
  const increaseQuantity = (quantity) => {
    const q = parseInt(quantity, 10) || 1;
    setQuantity(q);
    syncQtyToCart(q);
  };

  /** Decrements product quantity, flooring at 1. */
  const decreaseQuantity = (quantity) => {
    const q = quantity < 1 ? 1 : (parseInt(quantity, 10) || 1);
    setQuantity(q);
    syncQtyToCart(q);
  };

  // ── URL-based product lookup ──────────────────────────────────────────────
  const location = useLocation();
  const PathName = location.pathname;
  const productHandle = PathName.substring(PathName.lastIndexOf('/') + 1);
  const [Program_Types__c_color, setProgram_Types__c_color] = useState('#bc318c');  

  const { loading, error, data } = useQuery(FetchProductData, {
    variables: { handle: productHandle },
  });

  /**
   * Processes the Shopify product response and populates all related state.
   * Parses the 'all_data' metafield JSON, matches program structure images/colours,
   * and sets up variant options.
   *
   * @param {object} data - Apollo query result containing a `product` field.
   */
  const DataLoaded = useCallback((data) => {
    if (data.product !== undefined && data.product !== null && data.product.metafields !== null && data.product.metafields !== undefined) {
      let basic_data = {
        // Strip stray U+FFFD encoding artifacts from staging data (renders as "?").
        title: sanitizeText(data.product.title),
        description: sanitizeText(data.product.descriptionHtml),
        product_code: data.product.variants.edges[0].node.sku,
        price: data.product.priceRange !== undefined && data.product.priceRange.minVariantPrice !== undefined
          ? data.product.priceRange.minVariantPrice.amount
          : 0,
      };
      set_seo_title(sanitizeText(data.product.title));
      // Limit meta description to 157 characters (standard SEO length).
      set_seo_description(sanitizeText(data.product.description.substring(0, 157)));

      // Match the product's tags against Program_structure_images to find the card image.
      props.Program_structure_images.forEach(function (item) {
        var keyname = Object.keys(item);
        var tag_array = data.product.tags;
        if (tag_array.includes(keyname.toString())) {
          setProgram_structure_image(Object.values(item));
        }
      });

      // Match the product's tags against Program_structure_color to find the accent colour.
      props.Program_structure_color.forEach(function (item) {
        var keyname = Object.keys(item);
        var tag_array = data.product.tags;
        if (tag_array.includes(keyname.toString())) {
          setProgram_Types__c_color(Object.values(item));
        }
      });

      let new_data = JSON.parse(data.product.metafields[0].value);
      setRecordType(new_data.RecordType);
      setprogramType(new_data.Program_Types__c);
      setreached_capacity(new_data.Reached_Capacity__c);

      set_single_program_basic_data(basic_data);
      set_single_program_data(new_data);
    }

    if (data.product) {    

      if (data.product.metafields.length > 0) {
        setQuantity(1);
      }
    }
  },[props.Program_structure_images, props.Program_structure_color]);

  /** Triggers DataLoaded once Shopify returns the product. */
  useEffect(() => {
    if (data !== undefined) {
      DataLoaded(data);
    }
  }, [data,DataLoaded]);

  /** Kicks off a related-product fetch for each slug in relatedProducts. */
  useEffect(() => {
    if (relatedProducts.length > 0) {
      let related_product_count = 1;
      relatedProducts.forEach((related_val) => {
        related_product_count = related_product_count + 1;
      });
    }
  }, [relatedProducts]);

  // ── Social share helpers ──────────────────────────────────────────────────

  const [Clipboard, setClipboard] = useState("Copy Link to Clipboard");

  /**
   * Copies a URL string to the system clipboard using a temporary textarea.
   * Uses the legacy execCommand API for broad browser compatibility.
   *
   * @param {string} url
   */
  const copyToClipboard = (url) => {
    var textField = document.createElement('textarea');
    textField.innerText = url;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
  };

  /** Opens Facebook share dialog in a centred popup window. */
  const FBshare = () => {
    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + window.location.href + '&title=' + data.product.title,
      'fbShareWindow',
      'height=450, width=550, top=' + (window.innerHeight / 2 - 275) + ', left=' + (window.innerWidth / 2 - 225) + ', toolbar=0, location=0, menubar=0, directories=0, scrollbars=0'
    );
  };

  /** Opens Twitter/X share dialog in a centred popup window. */
  const TwitterShare = () => {
    window.open(
      'https://twitter.com/intent/tweet?url=' + window.location.href + '&text=' + data.product.title + ' ' + seo_description,
      'fbShareWindow',
      'height=450, width=550, top=' + (window.innerHeight / 2 - 275) + ', left=' + (window.innerWidth / 2 - 225) + ', toolbar=0, location=0, menubar=0, directories=0, scrollbars=0'
    );
  };

  /** Opens LinkedIn share dialog in a centred popup window. */
  const LinkedinShare = () => {
    window.open(
      'https://www.linkedin.com/shareArticle?mini=true&url=' + window.location.href + '&text=' + data.product.title,
      'fbShareWindow',
      'height=450, width=550, top=' + (window.innerHeight / 2 - 275) + ', left=' + (window.innerWidth / 2 - 225) + ', toolbar=0, location=0, menubar=0, directories=0, scrollbars=0'
    );
  };

  /** Composes a mailto: link with the current page URL as the body. */
  const emailShare = () => {
    const subject = encodeURIComponent(data.product.title);
    const body = encodeURIComponent(window.location.href);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  /** Copies the current page URL to clipboard and updates the button label. */
  const CopyLink = (e) => {
    copyToClipboard(window.location.href);
    setClipboard("Copied");
  };

  /** Resets the copy-to-clipboard button label after the user has moved away. */
  const RevetCopyText = () => {
    setClipboard("Copy Link to Clipboard");
  };

  /** Sets the first product variant as the selected variant once data loads. */
  useEffect(() => {
    if (data !== undefined && data.product !== undefined && data.product !== null) {
      setSingleVariant(data.product.variants.edges[0].node);
    }
  }, [data]);

  /**
   * Price-box height sync (desktop). The price box (.single_rightside) is
   * position:absolute on ≥992px, so it doesn't contribute to the page height.
   * Match the content column's min-height to the price box height so the page
   * never reserves a fixed block of empty space below it (short-content pages
   * like webcasts) nor lets the box overflow into the next section — without a
   * hard-coded min-height. Mobile (<992px) keeps normal flow (min-height cleared).
   */
  useEffect(() => {
    let ro;
    const sync = () => {
      const content = document.querySelector('.single_program_page .cpd_single_programs_content');
      const priceBox = document.querySelector('.single_program_page .single_rightside');
      if (!content || !priceBox) return;
      content.style.minHeight = window.innerWidth >= 992
        ? Math.ceil(priceBox.getBoundingClientRect().height) + 'px'
        : '';
    };
    const start = (tries = 0) => {
      const priceBox = document.querySelector('.single_program_page .single_rightside');
      if (!priceBox) { if (tries < 40) setTimeout(() => start(tries + 1), 100); return; }
      sync();
      if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(sync); ro.observe(priceBox); }
    };
    start();
    window.addEventListener('resize', sync);
    return () => { window.removeEventListener('resize', sync); if (ro) ro.disconnect(); };
  }, [single_program_data]);

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return <Container><div className="pre_loader"><LoaderIcon /></div></Container>;
  }
  if (error) {
    return <Container><h1>{JSON.stringify(error)}</h1></Container>;
  }

  
  

  /**
   * Returns true if the program starts within the next 10 minutes.
   * Used to show "Bookings Closed" slightly before the event actually begins.
   *
   * @param {string|null} program_start_date_time - ISO date string or null.
   * @returns {boolean}
   */
  const is_ten_minite_less = (program_start_date_time) => {
    if (program_start_date_time == null) {
      return false;
    }
    program_start_date_time = new Date(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(program_start_date_time.getHours());
    let current_time_10 = new Date(new Date().getTime() + 1000 * 600);
    let current_time = new Date().getTime();

    var from = current_time;
    var to = current_time_10;
    var check = program_time_modify;

    return from <= check && check <= to;
  };

  /**
   * Returns true if the program start date/time is in the past.
   * Used to show "Bookings Closed" on expired programs.
   *
   * @param {string|null|undefined} program_start_date_time - ISO date string, null, or undefined.
   * @returns {boolean}
   */
  const program_date_is_gone_or_not = (program_start_date_time) => {
    if (program_start_date_time === undefined || program_start_date_time === null || program_start_date_time === "") {
      return false;
    }
    program_start_date_time = new Date(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(program_start_date_time.getHours());
    let current_time = new Date().getTime();
    if ((parseFloat(program_time_modify) - parseFloat(current_time)) > 0) {
      return false;
    } else {
      return true;
    }
  };

  // ── Render — delegates to a type-specific layout component ────────────────
  if (
    data !== undefined &&
    data.product !== undefined &&
    data.product !== null &&
    single_program_data !== undefined &&
    single_program_basic_data !== undefined &&
    Object.keys(single_program_data).length > 0 &&
    Object.keys(single_program_basic_data).length > 0
  ) {
    return (
      <>
        <Helmet>
          <title>{seo_title} | Leo Cussen Centre for Law</title>
          <link rel="canonical" href={"https://store.leocussen.edu.au/product/" + data.product.handle} />
          <meta name="description" content={seo_description} />
        </Helmet>

        {/* CPD Programs layout */}
        {recordType !== undefined && recordType === 'CPD Programs' ? (
          <TypeProgram
            tags={data.product.tags}
            handle={data.product.handle}
            single_program_data={single_program_data}
            single_program_basic_data={single_program_basic_data}
            AddtoCart={props.AddtoCart}
            SingleVariants={SingleVariants}
            FBshare={FBshare}
            TwitterShare={TwitterShare}
            LinkedinShare={LinkedinShare}
            emailShare={emailShare}
            CopyLink={CopyLink}
            Clipboard={Clipboard}
            recordType={recordType}
            programType={programType}
            RevetCopyText={RevetCopyText}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
            quantity={quantity}
            Program_structure_image={Program_structure_image}
            product_page_type="leo_product_programs"
            reached_capacity={reached_capacity}
            is_ten_minite_less={is_ten_minite_less}
            getAllWishlist={props.getAllWishlist}
            AddtoWishlist={props.AddtoWishlist}
            Program_Types__c_color={Program_Types__c_color}
            program_date_is_gone_or_not={program_date_is_gone_or_not}
          />
        ) : null}

        {/* Book layout */}
        {recordType !== undefined && recordType === 'Book' ? (
          <TypeBook
            tags={data.product.tags}
            handle={data.product.handle}
            single_program_data={single_program_data}
            single_program_basic_data={single_program_basic_data}
            AddtoCart={props.AddtoCart}
            SingleVariants={SingleVariants}
            FBshare={FBshare}
            TwitterShare={TwitterShare}
            LinkedinShare={LinkedinShare}
            emailShare={emailShare}
            CopyLink={CopyLink}
            recordType={recordType}
            programType={programType}
            Clipboard={Clipboard}
            RevetCopyText={RevetCopyText}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
            Program_structure_image={Program_structure_image}
            quantity={quantity}
            product_page_type="leo_product_book"
            reached_capacity={reached_capacity}
            getAllWishlist={props.getAllWishlist}
            AddtoWishlist={props.AddtoWishlist}
            Program_Types__c_color={Program_Types__c_color}
            program_date_is_gone_or_not={program_date_is_gone_or_not}
          />
        ) : null}

        {/* Online Intensive (webcast) layout */}
        {recordType !== undefined && recordType === 'Online Intensive' ? (
          <TypeWebcast
            tags={data.product.tags}
            handle={data.product.handle}
            single_program_data={single_program_data}
            single_program_basic_data={single_program_basic_data}
            priceRange={priceRange}
            compareAtPriceRange={compareAtPriceRange}
            AddtoCart={props.AddtoCart}
            SingleVariants={SingleVariants}
            FBshare={FBshare}
            recordType={recordType}
            programType={programType}
            TwitterShare={TwitterShare}
            LinkedinShare={LinkedinShare}
            emailShare={emailShare}
            CopyLink={CopyLink}
            Clipboard={Clipboard}
            RevetCopyText={RevetCopyText}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
            Program_structure_image={Program_structure_image}
            quantity={quantity}
            product_page_type="leo_product_webcast"
            reached_capacity={reached_capacity}
            is_ten_minite_less={is_ten_minite_less}
            getAllWishlist={props.getAllWishlist}
            AddtoWishlist={props.AddtoWishlist}
            Program_Types__c_color={Program_Types__c_color}
            program_date_is_gone_or_not={program_date_is_gone_or_not}
          />
        ) : null}

        {/* Fallback — any unrecognised record type uses the CPD Programs layout */}
        {recordType !== 'Online Intensive' && recordType !== 'Book' && recordType !== 'CPD Programs' ? (
          <TypeProgram
            tags={data.product.tags}
            handle={data.product.handle}
            single_program_data={single_program_data}
            single_program_basic_data={single_program_basic_data}
            priceRange={priceRange}
            compareAtPriceRange={compareAtPriceRange}
            AddtoCart={props.AddtoCart}
            SingleVariants={SingleVariants}
            FBshare={FBshare}
            recordType={recordType}
            programType={programType}
            TwitterShare={TwitterShare}
            LinkedinShare={LinkedinShare}
            emailShare={emailShare}
            CopyLink={CopyLink}
            Clipboard={Clipboard}
            RevetCopyText={RevetCopyText}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
            Program_structure_image={Program_structure_image}
            quantity={quantity}
            product_page_type="leo_product_programs"
            reached_capacity={reached_capacity}
            is_ten_minite_less={is_ten_minite_less}
            getAllWishlist={props.getAllWishlist}
            AddtoWishlist={props.AddtoWishlist}
            Program_Types__c_color={Program_Types__c_color}
            program_date_is_gone_or_not={program_date_is_gone_or_not}
          />
        ) : null}
      </>
    );
  }

  if (data.product == null) {
    return (
      <div className="pre_loader text-center"><h2>Product Not Found.</h2></div>
    );
  }

  return (
    <div className="pre_loader text-center"><LoaderIcon /></div>
  );
};
